import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Customer } from '@app/core/models/customer.model';
import { OrderCreatorService } from '@shared/services/order-creator.service';
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged } from 'rxjs';
import { SubSink } from 'subsink';
import { LoadingOverlayService } from '@shared/services/loading-overlay.service';

@Component({
	selector: 'app-customers-list',
	templateUrl: './customers-list.component.html',
	styleUrls: ['./customers-list.component.css'],
	animations: [
		trigger(
			'expandCollapseAnimationContent', [
				transition(
					':enter', [
						style({ height: '0' }),
						animate('0.5s ease', style({ height: '*' }))
					]
				)
			]
		)
	],
})
export class CustomersListComponent implements OnInit, OnDestroy {

	// Note for 'no advertisers' notification:
	// As the list of customers is provided by the parent component, the 'customers$ === undefined' is considered a component loading state, and 'customers$ === []' is considered a no-advertisers state.
	// Also 'showToolsPanel == true' is considrerd a show-all mode, while '!showToolsPanel' means show-recent.
	// Separately, in the filtering mode only, the isFilteredCustomersListEmpty var is used to show/hide the notification.

	private readonly COMPONENT_NAME = "CustomersListComponent";

	@Output()
	customerSelected = new EventEmitter<Customer>();

	@Input()
	showToolsPanel = false;

	@Input("choose")
	get choose() { return this._choose };
	set choose(value: any) { this._choose = (value != null && `${value}` !== 'false') };
	private _choose: boolean = false;

	@Input("chooseAgency")
	get chooseAgency() { return this._chooseAgency };
	set chooseAgency(value: any) { this._chooseAgency = (value != null && `${value}` !== 'false') };
	private _chooseAgency: boolean = false;

	@Input() customers$: Observable<Customer[] | undefined> | undefined;
	private _allCustomers: Customer[] = [];

	@Input("expand")
	get forceExpand() { return this._forceExpand };
	set forceExpand(value: any) { this._forceExpand = (value != null && `${value}` !== 'false') };
	private _forceExpand: boolean = false;

	private _agency: Customer | undefined;

	searchControl = new FormControl();
	toolsForm = new FormGroup({
		searchControl: this.searchControl,
	});

	filteredCustomers: Customer[] = [];

	// used to show 'no advertisers' notification in filtering mode only
	isFilteredCustomersListEmpty: boolean = false;

	constructor (
		private _orderCreator: OrderCreatorService,
		private _router: Router,
		private _progressService: LoadingOverlayService
	) {
	}

	private subs = new SubSink();

	private readonly _callingOut$ = new BehaviorSubject<boolean>(false);

	ngOnInit(): void {
		this.subs.add(
			this._callingOut$.pipe(distinctUntilChanged()).subscribe(show => {
				if (show) {
					this._progressService.showIndeterminateHorizontalProgressIndicator();
				}
				else {
					this._progressService.hide();
				}
			}));

		if (this.customers$) {
			this.subs.add(
				this.customers$.subscribe({
					next: (customers) => {
						if (customers) {
							this._allCustomers = customers;
							this.resetFilter();
						}
					}
				}));
		}

		this.subs.add(
			this.searchControl.valueChanges
				.pipe(
					debounceTime(500),
					distinctUntilChanged()
				).subscribe({
					next: (val: string) => {
						if (val) {
							const upperCaseFilterValue = val.toUpperCase();
							this.filteredCustomers = this._allCustomers.filter((customer: Customer) => customer.name?.toUpperCase().includes(upperCaseFilterValue));
						}
						else {
							this.filteredCustomers = [...this._allCustomers];
						}

						this.isFilteredCustomersListEmpty = this.filteredCustomers.length === 0;
					}
				}));
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	getDefaultContact(customer: Customer | undefined) {
		if (customer != undefined && customer.contacts != undefined) {
			for (let i = 0; i < customer.contacts.length; i++) {
				let contact = customer.contacts[i];
				if (contact.default) return contact;
			}
		}
		return undefined;
	}

	createOrder(customer: Customer) {
		if (this.choose) {
			this.customerSelected.emit(customer);
		}
		else {
			this._callingOut$.next(true);
			this.subs.add(
				this._orderCreator.init().subscribe({
					next: () => {
						this._callingOut$.next(false);
						if (customer && customer.id && customer.name) {
							if (customer.agency) {
								this._orderCreator.createOrderWithAgency(customer);
							}
							else {
								this._orderCreator.createOrderWithAdvertiser(customer.id || "", customer.name || "");
							}
						}
					},
					error: (error) => {
						this._callingOut$.next(false);
					}
				})
			);
		}
	}

	showOrders(customer: Customer) {
		this._router.navigate(["/myads"], { queryParams: { advertiserId: customer.id } });
	}

	showCustomer(customer: Customer) {
		this._router.navigate(["/advertiser"], { state: { agency: this._agency, customer: customer } });
	}

	resetFilter(): void {
		// update the list immediately, don't go via debounceTime()
		this.searchControl.setValue("", { emitEvent: false });
		this.filteredCustomers = [...this._allCustomers];
		this.isFilteredCustomersListEmpty = false;
	}
}
